import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Money } from '../ui/Money';

const API = process.env.REACT_APP_BACKEND_URL;
const SRC = { live: 'Live', payroll: 'Saved payroll', snapshot: 'Saved dept total', estimate: 'Estimated' };
const SRC_TONE = { live: 'text-blue-700', payroll: 'text-emerald-700', snapshot: 'text-gray-600', estimate: 'text-amber-700' };

// 12 months ending at the selected month: salary bars + headcount line, with joiners/leavers.
export const DepartmentTrend = ({ department, month, year }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    axios.get(`${API}/api/payroll/comparison/department/trend`, { params: { department, month, year } })
      .then((r) => setRows(r.data.months)).catch(() => setRows([]));
  }, [department, month, year]);
  if (!rows) return <p className="text-sm text-gray-400 py-8 text-center">Loading trend…</p>;
  if (!rows.length) return <p className="text-sm text-red-600 py-8 text-center">Could not load the trend.</p>;
  const first = rows[0]; const last = rows[rows.length - 1];
  const hcDiff = last.headcount - first.headcount;
  const salDiff = last.salary - first.salary;
  return (
    <div className="space-y-3" data-testid="dept-trend">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <div className="rounded-lg border p-2.5 bg-violet-50/50"><p className="text-[11px] text-gray-500">Headcount now</p><p className="text-lg font-semibold" data-testid="dept-trend-headcount">{last.headcount}</p></div>
        <div className="rounded-lg border p-2.5"><p className="text-[11px] text-gray-500">vs {first.label}</p><p className={`text-lg font-semibold ${hcDiff > 0 ? 'text-red-600' : hcDiff < 0 ? 'text-green-600' : ''}`}>{hcDiff > 0 ? '+' : ''}{hcDiff} staff</p></div>
        <div className="rounded-lg border p-2.5 bg-blue-50/50"><p className="text-[11px] text-gray-500">Salary {last.label}</p><p className="text-lg font-semibold text-blue-700"><Money value={last.salary} /></p></div>
        <div className="rounded-lg border p-2.5"><p className="text-[11px] text-gray-500">12-month change</p><p className={`text-lg font-semibold ${salDiff > 0 ? 'text-red-600' : salDiff < 0 ? 'text-green-600' : ''}`}>{salDiff > 0 ? '+' : salDiff < 0 ? '−' : ''}<Money value={Math.abs(salDiff)} /></p></div>
      </div>
      <div className="h-64" data-testid="dept-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="s" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
            <YAxis yAxisId="h" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n) => (n === 'Salary (AED)' ? Number(v).toLocaleString('en-US') : v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="s" dataKey="salary" name="Salary (AED)" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            <Line yAxisId="h" type="monotone" dataKey="headcount" name="Headcount" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-xs" data-testid="dept-trend-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-gray-500"><tr>
            {['Month', 'Headcount', 'Joined', 'Left', 'Salary', 'Source'].map((h) => <th key={h} className={`px-3 py-1.5 ${h === 'Month' || h === 'Source' ? 'text-left' : 'text-right'}`}>{h}</th>)}
          </tr></thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={r.label} data-testid={`dept-trend-row-${r.year}-${r.month}`}>
                <td className="px-3 py-1.5 font-medium">{r.label}</td>
                <td className="px-3 py-1.5 text-right">{r.headcount}</td>
                <td className="px-3 py-1.5 text-right text-emerald-700">{r.joiners ? `+${r.joiners}` : '—'}</td>
                <td className="px-3 py-1.5 text-right text-red-600">{r.leavers ? `−${r.leavers}` : '—'}</td>
                <td className="px-3 py-1.5 text-right"><Money value={r.salary} /></td>
                <td className={`px-3 py-1.5 ${SRC_TONE[r.source]}`}>{SRC[r.source]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-500">Headcount is rebuilt from joining and exit dates (staff counted in their current department). Salary uses saved payroll where available; "Estimated" months use current salaries.</p>
    </div>
  );
};
